import React, { useState, useEffect } from 'react';

import axios from 'axios';
import { useForm } from 'react-hook-form';
import { navigate } from 'gatsby';

// Components
import Input from './elements/Input';
import Select from './elements/Select';
import RichTextRenderer from '../richTextRenderer';
import UtmData from '../../scripts/UtmData';
// Utils
import Translate from '../../utils/translate';
// Images
import LoadingIndicator from '../../images/form/LoadingIndicator';
// Interfaces
import { Form as FormInterface, Input as InputInterface } from '../../interfaces/form';

function Form({
  data,
  partnerCode,
  gclidInputValue,
}: {
  data: FormInterface;
  partnerCode?: string;
  gclidInputValue?: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const {
    apiEndpoint,
    apiTokenEndpoint,
    devApiEndpoint,
    devApiTokenEndpoint,
    uatApiEndpoint,
    uatApiTokenEndpoint,
    formRedirect,
    formId,
    formElements,
    text,
    consent,
    extraClasses,
  } = data;

  const [isProduction, setIsProduction] = useState(true);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isDevEnvironment =
        process.env['NODE_ENV'] === 'development' || window.location.href.includes('dev');
      setIsProduction(!isDevEnvironment);
    }
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const utmParamsData = UtmData();

  const onSubmit = async (formData: any) => {
    const environment = formData.environment;
    let selectedApiEndpoint = apiEndpoint;
    let selectedApiTokenEndpoint = apiTokenEndpoint;

    if (environment === 'dev') {
      selectedApiEndpoint = devApiEndpoint;
      selectedApiTokenEndpoint = devApiTokenEndpoint;
    }
    if (environment === 'uat') {
      selectedApiEndpoint = uatApiEndpoint;
      selectedApiTokenEndpoint = uatApiTokenEndpoint;
    }

    // Token fetching
    const getToken = async () => {
      setLoading(true);
      try {
        return await axios.get(selectedApiTokenEndpoint);
      } catch (error) {
        setLoading(false);
        setError(true);
      }
    };

    // Form submission
    if (formId === 'send-partner-form') {
      setLoading(true);
      try {
        const spfResponse = await axios.post(selectedApiEndpoint, formData, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        console.log('spfResponse', spfResponse);
        setLoading(false);
        if (spfResponse.status === 200) {
          navigate(formRedirect);
        }
      } catch (error) {
        setLoading(false);
        setError(true);
        console.error('Error submitting partner form:', error);
      }
      return;
    }

    const complete = formData.locale === 'GB';
    const token = await getToken();

    let ipAddress;
    try {
      const response = await axios.get('https://api.ipify.org?format=json');
      ipAddress = response.data.ip;
    } catch (error) {
      console.error('Error fetching IP address:', error);
    }

    const submitData =
      formId === 'signup-form' || formId === 'partner-signup'
        ? { ...formData, ...utmParamsData, complete, ipAddress }
        : { ...formData };

    const axiosConfig = {
      headers: {},
    };

    if (token) {
      axiosConfig.headers = {
        'X-Token': token.data.ID,
      };
    }

    axios
      .post(selectedApiEndpoint, submitData, axiosConfig)
      .then(() => {
        setLoading(false);
        navigate(formRedirect);
      })
      .catch((error) => {
        setLoading(false);
        setError(true);
        console.error('Error submitting form:', error);
      });
  };

  const inputsRenderer = (inputs: Array<InputInterface>) => {
    return inputs.map((input: InputInterface | any, index: number) => {
      const isEnvironmentDropdown = input.id === '51a2d2fc-7881-55a8-ab41-b33f20007f2c';

      if (input.type) {
        switch (input.type) {
          case 'select':
            return (
              <Select
                data={input}
                errors={errors}
                register={register}
                key={index}
                extraClasses={isEnvironmentDropdown && isProduction ? 'hidden' : ''}
              />
            );
          default:
            return <Input data={input} errors={errors} register={register} key={index} />;
        }
      } else {
        return (
          <div className='cl-form-field__submit' key={index}>
            <button
              disabled={loading ? true : false}
              className={`disabled:cursor-not-allowed  cl-button cl-button--${input.size} cl-button--${input.color} cl-button--${input.variation} cl-button--${input.elevation}`}
            >
              {input.link?.linkName}

              {loading && <LoadingIndicator />}
            </button>

            {error && (
              <p className='cl-form__error'>
                Unable to process signup, please call us for more info on <br />{' '}
                <a
                  href='tel:03330506070'
                  target='_blank'
                  rel='noreferrer'
                  aria-label='03330 50 60 70'
                  className='!text-primary-600 hover:!text-primary-700 underline underline-offset-2'
                >
                  0333 050 6070
                </a>{' '}
                (Press Option 1) or{' '}
                <a
                  href='/request-callback'
                  className='!text-primary-600 hover:!text-primary-700 underline underline-offset-2'
                >
                  Request a Callback
                </a>
              </p>
            )}
            {/* Consent */}
            {consent && (
              <small>
                <RichTextRenderer text={consent} />
              </small>
            )}
          </div>
        );
      }
    });
  };

  return (
    <div className={`cl-form ${extraClasses ? extraClasses : ''}`}>
      {text && (
        <div className='cl-form__heading'>
          <RichTextRenderer text={text} />
        </div>
      )}
      <form noValidate onSubmit={handleSubmit(onSubmit)} id={formId}>
        {formElements.map((formElement, index: number) => (
          <div
            className={`cl-form__fields ${
              formElement.extraClasses ? formElement.extraClasses : ''
            }`}
            key={index}
          >
            {inputsRenderer(formElement.inputs)}

            {/* Partner Code Hidden Input Field */}
            {partnerCode && (
              <div className='cl-form-field--hidden'>
                <input
                  type='text'
                  {...register('partnerCode', {
                    required: {
                      value: true,
                      message: 'Field is required',
                    },
                    minLength: {
                      value: 2,
                      message: 'At least two characters are required',
                    },
                  })}
                  className='cl-form-field__input peer'
                  hidden={true}
                  value={partnerCode}
                />
              </div>
            )}
            {/* gclid Hidden Input Field */}
            {gclidInputValue && (
              <div className='cl-form-field--hidden'>
                <input
                  type='text'
                  {...register('gclid_field', {
                    required: {
                      value: true,
                      message: 'Field is required',
                    },
                    minLength: {
                      value: 2,
                      message: 'At least two characters are required',
                    },
                  })}
                  className='cl-form-field__input peer'
                  hidden={true}
                  value={gclidInputValue}
                  id='gclid_field'
                />
              </div>
            )}
          </div>
        ))}
      </form>
    </div>
  );
}

export default Form;
